import React, { useState, useEffect } from 'react';
import { 
Container, 
Typography, 
Button, 
Paper, 
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
TablePagination,
Card,
CardActions,
Divider,
useTheme,
Menu,
Alert,
Switch,
FormControlLabel,
Radio,
RadioGroup,
FormLabel,
FormHelperText
} from '@mui/material';
import { 
Delete, 
Search,
Add as AddIcon,
CloudUpload,
Close as CloseIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
Today as TodayIcon,
Edit as EditIcon,
Visibility as ViewIcon,
Key as KeyIcon,
GetApp,
Person,
CheckCircle,
Cancel,
Business,
People as PeopleIcon,
Person as PersonIcon,
Restaurant as RestaurantIcon,
LocalBar as LocalBarIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import useCustomToast from '../../hooks/useToast';
import { 
    APIUserStore, 
    APIUserList, 
    APIUserUpdate, 
    APIUserDelete, 
    APIUserUpdatePassword,
    APICounterNames
} from '../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../services/Text';
import { formatCreatedAtDate, formatNumberWithComma, formatNumberWithComma2, getUserDetails, recordsPerPage, roleOptions, statusData } from '../../utils/helpers';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../utils/Permission';

// Hotel role options
const hotelRoleOptions = [
    { id: 'bar', name: 'Bar User', icon: <LocalBarIcon /> },
    { id: 'restaurant', name: 'Restaurant User', icon: <RestaurantIcon /> }
];

const Users = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [counterLoading, setCounterLoading] = useState(false);
    const [error, setError] = useState(null);
    const [counterError, setCounterError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [counters, setCounters] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // User type selection
    const [userType, setUserType] = useState('club');
    
    // Updated form data to include hotel_role
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        role: '',
        counter_id: '',
        hotel_role: '', // New field for hotel user role
        password: '',
        confirmPassword: '',
        status: '1',
        user_type: 'club'
    });

    const [passwordFormData, setPasswordFormData] = useState({
        password: '',
        confirmPassword: ''
    });

    const [filters, setFilters] = useState({
        name: '',
        email: '',
        role: '',
        counter_id: '',
        user_type: '',
        hotel_role: '', // New filter for hotel role
        status: '',
        dateFrom: '',
        dateTo: ''
    });

    const [errors, setErrors] = useState({
        name: false,
        email: false,
        role: false,
        counter_id: false,
        hotel_role: false, // New error field
        user_type: false,
        password: false,
        confirmPassword: false,
        status: false
    });

    const [passwordErrors, setPasswordErrors] = useState({
        password: false,
        confirmPassword: false
    });

    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openChangePassword, setOpenChangePassword] = useState(false);

    // Permissions
    const [userRole, setUserRole] = useState('');
    useEffect(() => {
        const role = getUserRole();
        setUserRole(role);
    }, []);

    // Helper function
    const hasPermission = (permission) => {
        return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };

    // Function to get role name from role ID
    const getRoleName = (roleId) => {
        if (!roleId) return "Not Set";
        const role = roleOptions.find(item => item.id === roleId);
        return role?.name || roleId;
    };

    // Function to get role color
    const getRoleColor = (roleId) => {
        switch(roleId) {
            case 'SA': return 'primary';
            case 'AD': return 'secondary';
            case 'MA': return 'success';
            case 'CA': return 'warning';
            case 'ST': return 'info';
            default: return 'default';
        }
    };

    // Function to get counter name from counter ID
    const getCounterName = (counterId) => {
        if (!counterId) return "Not Assigned";
        const counter = counters.find(item => item.id.toString() === counterId.toString());
        return counter ? `${counter.name}` : counterId;
    };

    // Function to get counter color
    const getCounterColor = (counterId) => {
        if (!counterId) return "default";
        const counterIndex = counters.findIndex(item => item.id.toString() === counterId.toString());
        const colors = ['primary', 'secondary', 'success', 'warning', 'info', 'error'];
        return colors[counterIndex % colors.length] || 'default';
    };

    // Function to get user type display text
    const getUserTypeText = (userType) => {
        return userType === 'club' ? 'Club User' : 'Hotel User';
    };

    // Function to get user type icon
    const getUserTypeIcon = (userType) => {
        return userType === 'club' ? <PeopleIcon /> : <PersonIcon />;
    };

    // Function to get user type color
    const getUserTypeColor = (userType) => {
        return userType === 'club' ? 'primary' : 'secondary';
    };

    // Function to get hotel role display text
    const getHotelRoleText = (hotelRole) => {
        if (!hotelRole) return "Not Set";
        const role = hotelRoleOptions.find(item => item.id === hotelRole);
        return role?.name || hotelRole;
    };

    // Function to get hotel role icon
    const getHotelRoleIcon = (hotelRole) => {
        if (!hotelRole) return <PersonIcon />;
        const role = hotelRoleOptions.find(item => item.id === hotelRole);
        return role?.icon || <PersonIcon />;
    };

    // Function to get hotel role color
    const getHotelRoleColor = (hotelRole) => {
        switch(hotelRole) {
            case 'bar': return 'info';
            case 'restaurant': return 'warning';
            default: return 'default';
        }
    };

    // Fetch counters from API
    const fetchCounters = async () => {
        setCounterLoading(true);
        setCounterError(null);

        try {
            const response = await axios.get(APICounterNames);
            if (response.data.status === "success") {
                const fetchedCounters = Array.isArray(response.data.data) ? response.data.data : [];
                setCounters(fetchedCounters);
            } else {
                setCounterError("Failed to load counters");
                console.error("Counter fetch error:", response.data.message);
            }
        } catch (error) {
            let errorMessage = "Failed to load counters";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setCounterError(errorMessage);
            console.error("Counter API Error:", error);
        } finally {
            setCounterLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        fetchCounters();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");
            const response = await axios.get(APIUserList, {params: {branch_id: userBranchId}});
            const users = Array.isArray(response.data.data) ? response.data.data.reverse() : [];
            setPageState(prev => ({
                ...prev,
                pageData: users
            }));

            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";

            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }

            setError(errorMessage);
            console.log(errorMessage)
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    // Get status display text
    const getStatusText = (status) => {
        return status === '1' || status === 1 ? 'Active' : 'Not-Active';
    };

    // Get status color
    const getStatusColor = (status) => {
        return status === '1' || status === 1 ? 'success' : 'error';
    };

    // Get status icon
    const getStatusIcon = (status) => {
        return status === '1' || status === 1 ? <CheckCircle /> : <Cancel />;
    };

    // Filter based on search term and filters
    const filteredData = pageState.pageData.filter(record => {
        const roleName = getRoleName(record.role);
        const counterName = getCounterName(record.counter_id);
        const userTypeText = getUserTypeText(record.user_type);
        const hotelRoleText = getHotelRoleText(record.hotel_role);
        
        const matchesSearch = searchTerm === '' ||
            record.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            roleName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            counterName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            userTypeText.toLowerCase().includes(searchTerm.toLowerCase()) ||
            hotelRoleText.toLowerCase().includes(searchTerm.toLowerCase()) ||
            getStatusText(record.status).toLowerCase().includes(searchTerm.toLowerCase());

        // Name filter
        const matchesName = !filters.name ||
            record.name.toLowerCase().includes(filters.name.toLowerCase());
        
        const matchesEmail = !filters.email ||
            record.email.toLowerCase().includes(filters.email.toLowerCase());
        
        const matchesRole = !filters.role ||
            getRoleName(record.role).toLowerCase().includes(filters.role.toLowerCase());

        const matchesCounter = !filters.counter_id ||
            record.counter_id?.toString() === filters.counter_id.toString();

        const matchesUserType = !filters.user_type ||
            record.user_type === filters.user_type;

        const matchesHotelRole = !filters.hotel_role ||
            record.hotel_role === filters.hotel_role;

        // Status filter
        const matchesStatus = !filters.status ||
            record.status.toString() === filters.status;

        // Date range filter
        let matchesDate = true;
        if (filters.dateFrom) {
            const recordDate = new Date(record.created_at).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate >= filterFromDate;
        }

        if (filters.dateTo) {
            const recordDate = new Date(record.created_at).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate <= filterToDate;
        }

        return matchesSearch && matchesName && matchesEmail && matchesRole && matchesCounter && matchesUserType && matchesHotelRole && matchesStatus && matchesDate;
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenAdd = () => {
        setPostStatus("ADD")
        setUserType('club');
        setFormData({
            name: '',
            email: '',
            role: '',
            counter_id: '',
            hotel_role: '',
            password: '',
            confirmPassword: '',
            status: '1',
            user_type: 'club'
        });
        setOpenAdd(true);
    }

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    const handleCloseView = () => setOpenView(false);

    const handleOpenEdit = (record) => {
        setSelectedRecord(record);
        setUpdateId(record.id)
        setPostStatus("UPDATE")
        setUserType(record.user_type || 'club');

        // Set form data including status, user type, counter, and hotel role
        setFormData({
            name: record.name,
            email: record.email,
            role: record.role,
            counter_id: record.counter_id || '',
            hotel_role: record.hotel_role || '',
            password: '',
            confirmPassword: '',
            status: record.status.toString() || '1',
            user_type: record.user_type || 'club'
        });
        setOpenAdd(true);
    };

    const handleOpenChangePassword = (record) => {
        setSelectedRecord(record);
        setUpdateId(record.id)
        setPasswordFormData({
            password: '',
            confirmPassword: ''
        });
        setOpenChangePassword(true);
    };

    const handleCloseChangePassword = () => {
        setOpenChangePassword(false);
    };

    const handleOpenDelete = (record) => {
        setSelectedRecord(record);
        setUpdateId(record.id)
        setPostStatus("DELETE")
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false)
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Reset error for this field
        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // If user type changes, reset counter selection
        if (name === 'user_type' && value !== 'club') {
            setFormData(prev => ({
                ...prev,
                counter_id: ''
            }));
        }
    };

    const handleUserTypeChange = (e) => {
        const value = e.target.value;
        setUserType(value);
        setFormData(prev => ({
            ...prev,
            user_type: value,
            counter_id: value === 'club' ? prev.counter_id : '', // Clear counter if not club user
            hotel_role: value === 'other' ? prev.hotel_role : '' // Clear hotel role if club user
        }));
    };

    const handlePasswordInputChange = (e) => {
        const { name, value } = e.target;

        // Reset error for this field
        setPasswordErrors(prev => ({
            ...prev,
            [name]: false
        }));

        setPasswordFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Toggle status handler
    const handleStatusToggle = () => {
        const newStatus = formData.status === '1' ? '0' : '1';
        setFormData(prev => ({
            ...prev,
            status: newStatus
        }));
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            name: '',
            email: '',
            role: '',
            counter_id: '',
            user_type: '',
            hotel_role: '',
            status: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const validateForm = () => {
        const errors = [];
        if (!formData.name.trim()) errors.push('Name is required');
        if (!formData.email.trim()) errors.push('Email is required');
        if (!formData.role.trim()) errors.push('Role is required');
        
        // Role-specific validation
        if (formData.role === 'AD') {
            // Admin only needs user_type
            if (!formData.user_type.trim()) errors.push('User type is required');
        } else if (formData.role === 'ST') {
            // Staff needs user_type and specific selections
            if (!formData.user_type.trim()) errors.push('User type is required');
            
            // Only require counter for club users
            if (formData.user_type === 'club' && !String(formData.counter_id || "").trim()) {
                errors.push('Counter is required for Club Users');
            }

            // Only require hotel_role for hotel users
            if (formData.user_type === 'other' && !formData.hotel_role) {
                errors.push('Hotel role is required for Hotel Users');
            }
        }

        // For new users, password is required
        if (postStatus === "ADD" && !formData.password.trim()) {
            errors.push('Password is required for new users');
        }

        // Password confirmation
        if (formData.password && formData.password !== formData.confirmPassword) {
            errors.push('Passwords do not match');
        }

        // Email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailRegex.test(formData.email)) {
            errors.push('Please enter a valid email address');
        }

        if (errors.length > 0) {
            showToast("Validation Error", errors.join('\n'), "warning");
            return false;
        }
        return true;
    };

    const validatePasswordForm = () => {
        const errors = [];
        if (!passwordFormData.password.trim()) errors.push('Password is required');
        if (passwordFormData.password !== passwordFormData.confirmPassword) {
            errors.push('Passwords do not match');
        }
        if (passwordFormData.password.length < 6) {
            errors.push('Password must be at least 6 characters long');
        }

        if (errors.length > 0) {
            showToast("Validation Error", errors.join('\n'), "warning");
            return false;
        }
        return true;
    };

    const handleDialogClose = () => {
        setOpenAdd(false);
        setOpenView(false);
        setOpenDelete(false);
        setOpenChangePassword(false);
        setFilterOptions(false);
    };

    const handleFormDataReset = () => {
        setFormData({
            name: '',
            email: '',
            role: '',
            counter_id: '',
            hotel_role: '',
            password: '',
            confirmPassword: '',
            status: '1',
            user_type: 'club'
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        formDataToSend.append('name', formData.name);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('role', formData.role);
        formDataToSend.append('user_type', formData.user_type);
        
        // Only send counter_id for club users with staff role
        if (formData.role === 'ST' && formData.user_type === 'club') {
            formDataToSend.append('counter_id', formData.counter_id);
        }
        
        // Only send hotel_role for hotel users with staff role
        if (formData.role === 'ST' && formData.user_type === 'other') {
            formDataToSend.append('hotel_role', formData.hotel_role);
        }
        
        formDataToSend.append('status', formData.status);

        // Only append password if it's provided (for update) or for new users
        if (formData.password) {
            formDataToSend.append('password', formData.password);
        }

        // Only append updateId for UPDATE/DELETE operations
        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let apiUrl, method;
            if (postStatus === "ADD") {
                apiUrl = APIUserStore;
                method = 'post';
            } else if (postStatus === "UPDATE") {
                apiUrl = APIUserUpdate;
                method = 'post';
            } else if (postStatus === "DELETE") {
                apiUrl = APIUserDelete;
                method = 'post';
            }

            const response = await axios({
                method,
                url: apiUrl,
                data: formDataToSend
            });

            if (response.data.status === "success") {
                showToast("Success", response.data.message, "success");
                handleFormDataReset();
                handleDialogClose();
                fetchData();
            } else {
                showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
            }
        } catch (error) {
            if (error.response) {
                const { status, data } = error.response;
                if (status === 422) {
                    const errors = data.errors || {};
                    let errorMessages = [];
                    for (const field in errors) {
                        errorMessages.push(`${field}: ${errors[field].join(', ')}`);
                    }
                    showToast(
                        "Validation Error",
                        errorMessages.join('\n') || "Please correct the form errors",
                        "error"
                    );
                } else if (status === 409) {
                    showToast("Error", data.message || "This record already exists", "error");
                } else if (status >= 500) {
                    showToast("Server Error", data.message || "Internal server error", "error");
                } else {
                    showToast("Error", data.message || "Request failed", "error");
                }
            } else if (error.request) {
                showToast("Network Error", "No response from server. Please check your connection.", "error");
            } else {
                showToast("Error", error.message || "An unexpected error occurred", "error");
            }
            console.error("API Error:", error);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (!validatePasswordForm()) return;

        const formDataToSend = new FormData();
        formDataToSend.append('email', selectedRecord.email);
        formDataToSend.append('password', passwordFormData.password);
        formDataToSend.append('branch_id', getUserDetails("branchId"));

        try {
            const response = await axios({
                method: 'post',
                url: APIUserUpdatePassword,
                data: formDataToSend
            });

            if (response.data.status === "success") {
                showToast("Success", response.data.message, "success");
                handleDialogClose();
            } else if (response.data.status === "invalid") {
                showToast("Error", response.data.message || "Failed to update password", "error");
            } else {
                showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
            }
        } catch (error) {
            if (error.response) {
                const { status, data } = error.response;
                if (status === 422) {
                    const errors = data.errors || {};
                    let errorMessages = [];
                    for (const field in errors) {
                        errorMessages.push(`${field}: ${errors[field].join(', ')}`);
                    }
                    showToast(
                        "Validation Error",
                        errorMessages.join('\n') || "Please correct the form errors",
                        "error"
                    );
                } else if (status === 500) {
                    showToast("Error", data.message || "Internal server error", "error");
                } else {
                    showToast("Error", data.message || "Request failed", "error");
                }
            } else if (error.request) {
                showToast("Network Error", "No response from server. Please check your connection.", "error");
            } else {
                showToast("Error", error.message || "An unexpected error occurred", "error");
            }
            console.error("API Error:", error);
        }
    };

    // Check if user is Admin or Staff
    const isAdmin = userRole === 'AD';
    const isStaff = userRole === 'ST';
    
    // Determine if user can add/edit users
    const canManageUsers = hasPermission(PERMISSIONS.ADD) || hasPermission(PERMISSIONS.SETTINGS);
    
    // Determine if the role being created/edited is Admin
    const isCreatingAdmin = formData.role === 'AD';

    // Loading states
    if (loading || counterLoading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {/* Title */}
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Users Management ( {formatNumberWithComma2(pageState.pageData.length)} Total )
                    </Typography>

                    {/* Buttons - stays on left */}
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {canManageUsers && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                            >
                                Add New User
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<FilterIcon />}
                            onClick={handlefilterOptions}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                        >
                            View Options
                        </Button>
                    </Box>

                    {/* Search Bar - pushed to right with margin-left auto */}
                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters as a separate row below */}
                {(filters.name || filters.email || filters.role || filters.counter_id || filters.user_type || filters.hotel_role || filters.status || filters.dateFrom || filters.dateTo) && (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.name && (
                            <Chip
                                label={`Name: ${filters.name}`}
                                onDelete={() => setFilters(prev => ({ ...prev, name: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.email && (
                            <Chip
                                label={`Email: ${filters.email}`}
                                onDelete={() => setFilters(prev => ({ ...prev, email: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.role && (
                            <Chip
                                label={`Role: ${filters.role === 'AD' ? 'Admin' : 'Staff'}`}
                                onDelete={() => setFilters(prev => ({ ...prev, role: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.counter_id && (
                            <Chip
                                label={`Counter: ${getCounterName(filters.counter_id)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, counter_id: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.user_type && (
                            <Chip
                                icon={getUserTypeIcon(filters.user_type)}
                                label={`Type: ${getUserTypeText(filters.user_type)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, user_type: '' }))}
                                color={getUserTypeColor(filters.user_type)}
                                variant="outlined"
                            />
                        )}
                        {filters.hotel_role && (
                            <Chip
                                icon={getHotelRoleIcon(filters.hotel_role)}
                                label={`Hotel Role: ${getHotelRoleText(filters.hotel_role)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, hotel_role: '' }))}
                                color={getHotelRoleColor(filters.hotel_role)}
                                variant="outlined"
                            />
                        )}
                        {filters.status && (
                            <Chip
                                label={`Status: ${getStatusText(filters.status)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                                color={getStatusColor(filters.status)}
                                variant="outlined"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>User Type</TableCell>
                                    <TableCell>Counter/Hotel Role</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user, index) => (
                                    <TableRow
                                        key={index}
                                        hover
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Chip
                                                label={page * rowsPerPage + index + 1}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{formatCreatedAtDate(user.created_at)}</TableCell>
                                        <TableCell sx={{ fontWeight: 'medium' }}>
                                            {user.name}
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getRoleName(user.role)}
                                                color={getRoleColor(user.role)}
                                                variant="outlined"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getUserTypeIcon(user.user_type)}
                                                label={getUserTypeText(user.user_type)}
                                                color={getUserTypeColor(user.user_type)}
                                                variant="outlined"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {user.user_type === 'club' && user.role === 'ST' ? (
                                                <Chip
                                                    icon={<Business />}
                                                    label={getCounterName(user.counter_id)}
                                                    color={getCounterColor(user.counter_id)}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            ) : user.user_type === 'other' && user.role === 'ST' ? (
                                                <Chip
                                                    icon={getHotelRoleIcon(user.hotel_role)}
                                                    label={getHotelRoleText(user.hotel_role)}
                                                    color={getHotelRoleColor(user.hotel_role)}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            ) : user.role === 'AD' ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    Admin User
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    N/A
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getStatusIcon(user.status)}
                                                label={getStatusText(user.status)}
                                                color={getStatusColor(user.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                edge="end"
                                                onClick={() => handleOpenView(user)}
                                                color="primary"
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.info.light
                                                    }
                                                }}
                                            >
                                                <ViewIcon />
                                            </IconButton>

                                            {canManageUsers && (
                                                <>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleOpenEdit(user)}
                                                        color="secondary"
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.success.light
                                                            }
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleOpenChangePassword(user)}
                                                        color="warning"
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.warning.light
                                                            }
                                                        }}
                                                    >
                                                        <KeyIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleOpenDelete(user)}
                                                        color="error"
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.error.light
                                                            }
                                                        }}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {/* Pagination */}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredData.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <Search sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No users found</Typography>
                            <Typography variant="body2">Try adjusting your search query</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Options Dialog */}
            <Dialog
                open={filterOptions}
                onClose={handleCloseOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Users
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Users
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Name / Email
                        </Typography>

                        <TextField
                            fullWidth
                            label="Name"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                        />

                        <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            value={filters.email}
                            onChange={handleFilterChange}
                        />

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Role
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                name="role"
                                value={filters.role}
                                onChange={handleFilterChange}
                                label="Role"
                            >
                                <MenuItem value="">All Roles</MenuItem>
                                {roleOptions.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by User Type
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>User Type</InputLabel>
                            <Select
                                name="user_type"
                                value={filters.user_type}
                                onChange={handleFilterChange}
                                label="User Type"
                            >
                                <MenuItem value="">All Types</MenuItem>
                                <MenuItem value="club">Club User</MenuItem>
                                <MenuItem value="other">Hotel User</MenuItem>
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        {filters.user_type === 'club' && (
                            <>
                                <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                                    Filter by Counter
                                </Typography>

                                <FormControl fullWidth>
                                    <InputLabel>Counter</InputLabel>
                                    <Select
                                        name="counter_id"
                                        value={filters.counter_id}
                                        onChange={handleFilterChange}
                                        label="Counter"
                                    >
                                        <MenuItem value="">All Counters</MenuItem>
                                        {counters.map((counter, index) => (
                                            <MenuItem key={index} value={counter.id}>
                                                {counter.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}

                        {filters.user_type === 'other' && (
                            <>
                                <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                                    Filter by Hotel Role
                                </Typography>

                                <FormControl fullWidth>
                                    <InputLabel>Hotel Role</InputLabel>
                                    <Select
                                        name="hotel_role"
                                        value={filters.hotel_role}
                                        onChange={handleFilterChange}
                                        label="Hotel Role"
                                    >
                                        <MenuItem value="">All Hotel Roles</MenuItem>
                                        {hotelRoleOptions.map((role, index) => (
                                            <MenuItem key={index} value={role.id}>{role.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Status
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                label="Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                {statusData.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseOptions}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleCloseOptions}
                        color="primary"
                        variant="contained"
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Dialog */}
            <Dialog
                open={openAdd}
                onClose={handleCloseAdd}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>{postStatus === "ADD" ? "Add New User" : "Edit User"}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Full Name"
                                    name="name"
                                    value={formData.name}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    error={errors.name}
                                    helperText={errors.name ? "Name is required" : ""}
                                    required
                                />
                            </Grid>
                            <br></br>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    error={errors.email}
                                    helperText={errors.email ? "Valid email is required" : ""}
                                    required
                                />
                            </Grid>
                            <br></br>
                            
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        name="role"
                                        value={formData.role}
                                        autoComplete="off"
                                        onChange={handleInputChange}
                                        label="Role"
                                        required
                                    >
                                        {roleOptions.map((item, index) => (
                                            <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br></br>

                            {/* User Type Selection - Always show */}
                            <Grid item xs={12} sm={6}>
                                <FormControl component="fieldset" fullWidth>
                                    <FormLabel component="legend">User Type</FormLabel>
                                    <RadioGroup
                                        row
                                        name="user_type"
                                        value={formData.user_type}
                                        onChange={handleUserTypeChange}
                                    >
                                        <FormControlLabel 
                                            value="club" 
                                            control={<Radio />} 
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <PeopleIcon fontSize="small" />
                                                    <span>Club User</span>
                                                </Box>
                                            } 
                                        />
                                        <FormControlLabel 
                                            value="other" 
                                            control={<Radio />} 
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <PersonIcon fontSize="small" />
                                                    <span>Hotel User</span>
                                                </Box>
                                            } 
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <br></br>

                            {/* Show additional fields only for Staff role */}
                            {formData.role === 'ST' && (
                                <>
                                    {/* Counter Selection - Only for Club Users */}
                                    {formData.user_type === 'club' && (
                                        <>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Counter</InputLabel>
                                                    <Select
                                                        name="counter_id"
                                                        value={formData.counter_id}
                                                        autoComplete="off"
                                                        onChange={handleInputChange}
                                                        label="Counter"
                                                        required={formData.user_type === 'club'}
                                                        disabled={counterLoading}
                                                    >
                                                        {counters.map((counter, index) => (
                                                            <MenuItem key={index} value={counter.id}>
                                                                {counter.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {counterLoading && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Loading counters...
                                                        </Typography>
                                                    )}
                                                </FormControl>
                                            </Grid>
                                            <br></br>
                                        </>
                                    )}

                                    {/* Hotel Role Selection - Only for Hotel Users */}
                                    {formData.user_type === 'other' && (
                                        <>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Hotel Role</InputLabel>
                                                    <Select
                                                        name="hotel_role"
                                                        value={formData.hotel_role}
                                                        autoComplete="off"
                                                        onChange={handleInputChange}
                                                        label="Hotel Role"
                                                        required={formData.user_type === 'other'}
                                                    >
                                                        <MenuItem value="">Select Hotel Role</MenuItem>
                                                        {hotelRoleOptions.map((role, index) => (
                                                            <MenuItem key={index} value={role.id}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    {role.icon}
                                                                    <span>{role.name}</span>
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <br></br>
                                        </>
                                    )}
                                </>
                            )}

                            {/* Status Field - Switch for better UX */}
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="body1">Status:</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                                label={formData.status === '1' ? 'Active' : 'Inactive'}
                                                color={formData.status === '1' ? 'success' : 'error'}
                                                size="small"
                                            />
                                            <Switch
                                                checked={formData.status === '1'}
                                                onChange={handleStatusToggle}
                                                color={formData.status === '1' ? 'success' : 'error'}
                                            />
                                        </Box>
                                    </Box>
                                </FormControl>
                            </Grid>
                            <br></br>

                            {postStatus === "ADD" && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Password"
                                            name="password"
                                            type="password"
                                            value={formData.password}
                                            autoComplete="new-password"
                                            onChange={handleInputChange}
                                            error={errors.password}
                                            helperText={errors.password ? "Password is required" : ""}
                                            required={postStatus === "ADD"}
                                        />
                                    </Grid>
                                    <br></br>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Confirm Password"
                                            name="confirmPassword"
                                            type="password"
                                            value={formData.confirmPassword}
                                            autoComplete="new-password"
                                            onChange={handleInputChange}
                                            error={errors.confirmPassword}
                                            helperText={errors.confirmPassword ? "Passwords must match" : ""}
                                            required={postStatus === "ADD"}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Box>

                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button
                                color="error"
                                onClick={handleCloseAdd}
                                variant="contained"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                                disabled={counterLoading}
                            >
                                {postStatus === "ADD" ? "Add User" : "Update User"}
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Change Password Dialog */}
            <Dialog
                open={openChangePassword}
                onClose={handleCloseChangePassword}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Change Password for User</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handlePasswordSubmit} sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2">
                                Changing password for user with email: <b>{selectedRecord?.email}</b>
                            </Typography>
                        </Alert>
                        
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="New Password"
                                    name="password"
                                    type="password"
                                    value={passwordFormData.password}
                                    autoComplete="new-password"
                                    onChange={handlePasswordInputChange}
                                    error={passwordErrors.password}
                                    helperText="Minimum 6 characters required"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Confirm New Password"
                                    name="confirmPassword"
                                    type="password"
                                    value={passwordFormData.confirmPassword}
                                    autoComplete="new-password"
                                    onChange={handlePasswordInputChange}
                                    error={passwordErrors.confirmPassword}
                                    helperText="Must match the password above"
                                    required
                                />
                            </Grid>
                        </Grid>

                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button
                                color="error"
                                onClick={handleCloseChangePassword}
                                variant="outlined"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                                disabled={!passwordFormData.password || !passwordFormData.confirmPassword}
                            >
                                Update Password
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* View Dialog */}
            <Dialog
                open={openView}
                onClose={handleCloseView}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>User Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ p: 2 }}>
                            <Box>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Name: </b>{selectedRecord.name}</Typography>
                                </Grid>
                                <br></br>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Email: </b>{selectedRecord.email}</Typography>
                                </Grid>
                                <br></br>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Role: </b>
                                        <Chip
                                            label={getRoleName(selectedRecord.role)}
                                            color={getRoleColor(selectedRecord.role)}
                                            variant="outlined"
                                        />
                                    </Typography>
                                </Grid>
                                <br></br>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>User Type: </b>
                                        <Chip
                                            icon={getUserTypeIcon(selectedRecord.user_type)}
                                            label={getUserTypeText(selectedRecord.user_type)}
                                            color={getUserTypeColor(selectedRecord.user_type)}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Typography>
                                </Grid>
                                <br></br>
                                {selectedRecord.role === 'ST' && selectedRecord.user_type === 'club' && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2"><b>Counter: </b>
                                                <Chip
                                                    icon={<Business />}
                                                    label={getCounterName(selectedRecord.counter_id)}
                                                    color={getCounterColor(selectedRecord.counter_id)}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Typography>
                                        </Grid>
                                        <br></br>
                                    </>
                                )}
                                {selectedRecord.role === 'ST' && selectedRecord.user_type === 'other' && selectedRecord.hotel_role && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2"><b>Hotel Role: </b>
                                                <Chip
                                                    icon={getHotelRoleIcon(selectedRecord.hotel_role)}
                                                    label={getHotelRoleText(selectedRecord.hotel_role)}
                                                    color={getHotelRoleColor(selectedRecord.hotel_role)}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Typography>
                                        </Grid>
                                        <br></br>
                                    </>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Status: </b>
                                        <Chip
                                            icon={getStatusIcon(selectedRecord.status)}
                                            label={getStatusText(selectedRecord.status)}
                                            color={getStatusColor(selectedRecord.status)}
                                            size="small"
                                        />
                                    </Typography>
                                </Grid>
                                <br></br>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Created Date: </b>{formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                                </Grid>
                                <br></br>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Last Updated: </b>{formatCreatedAtDate(selectedRecord.updated_at)}</Typography>
                                </Grid>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseView}
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDelete}
                onClose={handleCloseDelete}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete User</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this user?
                    </Typography>
                    {selectedRecord && (
                        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {selectedRecord.name} <br /> 
                            <Typography variant="body2" color="text.secondary">
                                {selectedRecord.email}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Chip
                                    label={getRoleName(selectedRecord.role)}
                                    color={getRoleColor(selectedRecord.role)}
                                    variant="outlined"
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                <Chip
                                    icon={getUserTypeIcon(selectedRecord.user_type)}
                                    label={getUserTypeText(selectedRecord.user_type)}
                                    color={getUserTypeColor(selectedRecord.user_type)}
                                    variant="outlined"
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                {selectedRecord.role === 'ST' && selectedRecord.user_type === 'club' && (
                                    <Chip
                                        icon={<Business />}
                                        label={getCounterName(selectedRecord.counter_id)}
                                        color={getCounterColor(selectedRecord.counter_id)}
                                        variant="outlined"
                                        size="small"
                                        sx={{ mr: 1 }}
                                    />
                                )}
                                {selectedRecord.role === 'ST' && selectedRecord.user_type === 'other' && selectedRecord.hotel_role && (
                                    <Chip
                                        icon={getHotelRoleIcon(selectedRecord.hotel_role)}
                                        label={getHotelRoleText(selectedRecord.hotel_role)}
                                        color={getHotelRoleColor(selectedRecord.hotel_role)}
                                        variant="outlined"
                                        size="small"
                                        sx={{ mr: 1 }}
                                    />
                                )}
                                <Chip
                                    icon={getStatusIcon(selectedRecord.status)}
                                    label={getStatusText(selectedRecord.status)}
                                    color={getStatusColor(selectedRecord.status)}
                                    size="small"
                                />
                            </Box>
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseDelete}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        variant="contained"
                    >
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Users;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});